// BUILD-046 J1a (Customer association): the "Customer" filter of the toolbar's Filter popover (My Tasks, Shared Tasks, Team / Company).
//
// It is a CLIENT-SIDE display filter of the rows the page has loaded, exactly like the Team department filter: it keeps the rows of one customer, or the rows with no
// customer, or (the default) every row. Its choices are the customers the loaded rows carry, named as the rows show them (the server's current name, or the last known one),
// so the filter can never offer a customer no loaded task has. It is applied inside viewListModel.deriveViewList, before the search and the sort, and it narrows the
// ROWS of a view, never the labelled counts of the page (those come from the whole list). The default is "all" and nothing is stored: saved views are a later
// increment (J4).
//
// A customer the person chose that no loaded row carries any more (the list was reloaded, a task was completed) stays chosen and stays in the choices, so the
// control never shows a value it does not list, and an empty result reads as "no tasks match", not as a bug.
//
// Pure module: no React, no I/O. Erasable TypeScript; the only import is the pure display model.

import { customerLineOf, customerOf } from './customerDisplayModel';
import { stripTestMarker } from './displayNames';

export type CustomerFilter = { kind: 'any' } | { kind: 'none' } | { kind: 'customer'; customerId: string; label: string };

export const ANY_CUSTOMER: CustomerFilter = Object.freeze({ kind: 'any' } as const);

/** The choice values of the control: `any`, `none`, or a customer id. A customer id can never equal the other two (they are not valid ids). */
export const CUSTOMER_FILTER_ANY_VALUE = 'any';
export const CUSTOMER_FILTER_NONE_VALUE = 'none';

export interface CustomerFilterOption {
  /** `any`, `none` or the customer id. */
  value: string;
  label: string;
  /** How many loaded rows carry this choice (for `any`, all of them); informational. */
  count: number;
}

export function customerFilterActive(filter: CustomerFilter): boolean {
  return filter.kind !== 'any';
}

export function customerFilterValue(filter: CustomerFilter): string {
  return filter.kind === 'any' ? CUSTOMER_FILTER_ANY_VALUE : filter.kind === 'none' ? CUSTOMER_FILTER_NONE_VALUE : filter.customerId;
}

/** Keeps the rows the filter asks for. `any` keeps every row. A row shown in its minimal form carries no customer, so it counts as "no customer". */
export function filterByCustomer<T>(rows: readonly T[], filter: CustomerFilter): readonly T[] {
  if (filter.kind === 'any') return rows;
  if (filter.kind === 'none') return rows.filter((row) => customerOf(row) === null);
  return rows.filter((row) => customerOf(row)?.customerId === filter.customerId);
}

/** The distinct customers of the rows, named as the rows name them, in name order (numbers in order: "Lot 2" before "Lot 12"), with how many rows carry each. */
function distinctCustomers(rows: readonly unknown[]): { customerId: string; label: string; count: number }[] {
  const byId = new Map<string, { customerId: string; label: string; count: number }>();
  for (const row of rows) {
    const customer = customerOf(row);
    if (customer === null) continue;
    const seen = byId.get(customer.customerId);
    if (seen === undefined) byId.set(customer.customerId, { customerId: customer.customerId, label: customer.label, count: 1 });
    else seen.count += 1;
  }
  return [...byId.values()].sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' }) || a.customerId.localeCompare(b.customerId));
}

/** Whether any of the rows carries a customer. The Customer control is offered only then (or while a customer filter is active). */
export function hasAnyCustomer(rows: readonly unknown[]): boolean {
  return rows.some((row) => customerOf(row) !== null);
}

/**
 * The choices: All customers, No customer, then each customer the loaded rows carry. The chosen customer is always among them, even when no loaded row carries it (its count is 0),
 * so the control never shows a value it does not list.
 */
export function customerFilterOptions(rows: readonly unknown[], filter: CustomerFilter): CustomerFilterOption[] {
  const customers = distinctCustomers(rows);
  const withCustomer = customers.reduce((sum, j) => sum + j.count, 0);
  const options: CustomerFilterOption[] = [
    { value: CUSTOMER_FILTER_ANY_VALUE, label: 'Any customer', count: rows.length },
    { value: CUSTOMER_FILTER_NONE_VALUE, label: 'Without a customer', count: rows.length - withCustomer },
    // Sorted above by the stored name; SHOWN without the "(FICTIONAL)" test marker.
    ...customers.map((j) => ({ value: j.customerId, label: stripTestMarker(j.label), count: j.count })),
  ];
  if (filter.kind === 'customer' && !customers.some((j) => j.customerId === filter.customerId)) options.push({ value: filter.customerId, label: stripTestMarker(filter.label), count: 0 });
  return options;
}

/** The filter a choice stands for. A value that is not one of the choices leaves the filter as it was (nothing is guessed). */
export function chooseCustomerFilter(value: unknown, options: readonly CustomerFilterOption[], current: CustomerFilter): CustomerFilter {
  if (value === CUSTOMER_FILTER_ANY_VALUE) return ANY_CUSTOMER;
  if (value === CUSTOMER_FILTER_NONE_VALUE) return { kind: 'none' };
  if (typeof value !== 'string') return current;
  const option = options.find((o) => o.value === value);
  return option === undefined ? current : { kind: 'customer', customerId: option.value, label: option.label };
}

/** What the control says under the choices: the choices are the customers of the tasks loaded so far, which is every task only when the list is whole. */
export function customerFilterNote(complete: boolean): string | null {
  return complete ? null : 'Lists the customers on the tasks loaded so far.';
}

/** The three views that can have a customer filter, and the filter each holds. */
export type CustomerFilterView = 'my-work' | 'group-shared' | 'team-work';
export type CustomerFilters = Readonly<Record<CustomerFilterView, CustomerFilter>>;

export function initialCustomerFilters(): CustomerFilters {
  return { 'my-work': ANY_CUSTOMER, 'group-shared': ANY_CUSTOMER, 'team-work': ANY_CUSTOMER };
}

export function setCustomerFilter(filters: CustomerFilters, view: CustomerFilterView, next: CustomerFilter): CustomerFilters {
  const current = filters[view];
  if (current === undefined) return filters;
  if (current.kind === next.kind && customerFilterValue(current) === customerFilterValue(next)) return filters;
  return { ...filters, [view]: next };
}

// ═════════════════════════════ the searchable Customer picker (BUILD-046 cycle C, Edit 3) ═════════════════════════════
//
// The Filter popover no longer shows a native drop-down of every customer. It shows a search box, the two quick choices (All customers, No customer) and a short hint, and lists
// matches only after the person types (at least one character), at most eight at a time. Everything below is the pure part of that control; the components
// (CustomerFilterPicker.tsx, CustomerFilterChip.tsx) only draw it.
//
//   - OPTIONS are the distinct customers on the rows the view has LOADED and the person may see (a redacted row carries no customer). The page passes the view's whole
//     loaded set BEFORE the status tab narrows it, so a customer that is only on a Waiting or Completed task is still found. A HISTORICAL customer (inactive, not found,
//     shown by its last known name) is an option like any other: nothing is hidden or demoted because of the customer's state, the task's status or its age.
//   - SEARCH matches the customer NAME. A task view carries `{ customerId, label, state, lastKnown, missing }` only (customerDisplayModel.CustomerShown): no code, no place,
//     so the search covers names and nothing else is invented. Matching ignores case and accents, and every word typed must be found (in any order).
//   - The VALUE of the filter stays the stable customer id (`any`, `none` or a customer id, as before). The name is only for reading; it is never the key.
//   - The SCOPE sentence says which tasks the options come from, and for a list read page by page that loading more can add other customers: the control never claims
//     to search every task.

/** The visible name of the control and of its chip. */
export const CUSTOMER_FILTER_LABEL = 'Customer';
/** The most matches shown at once. */
export const CUSTOMER_FILTER_RESULT_LIMIT = 8;

/** A customer offered by the picker: exactly what the loaded rows show of it (its stable id, its name and what is known about its state). */
export interface CustomerChoiceOption {
  customerId: string;
  label: string;
  state?: 'active' | 'inactive';
  lastKnown?: true;
  missing?: true;
}

function textOrEmpty(value: unknown): string {
  return typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : '';
}

function optionOf(row: unknown): CustomerChoiceOption | null {
  const customer = customerOf(row);
  if (customer === null) return null;
  const option: CustomerChoiceOption = { customerId: customer.customerId, label: customer.label };
  if (customer.state !== undefined) option.state = customer.state;
  if (customer.lastKnown === true) option.lastKnown = true;
  if (customer.missing === true) option.missing = true;
  return option;
}

/**
 * The customers the picker offers: the distinct customers of ALL the given rows, in name order (numbers in order: "Lot 2" before "Lot 12"). `rowSets` are every list the view
 * has loaded (Team / Company: the open list and, when it was read, the completed list). Rows of any status and any age count; a customer that appears on several rows is
 * one option, named as a row that was checked against the customer list names it (a stored last-known name only when no row has the current one). The chosen customer is
 * always among the options, even when no loaded row carries it any more, so the control never shows a value it does not list.
 */
export function customerChoicesOf(rowSets: readonly (readonly unknown[])[], filter: CustomerFilter): CustomerChoiceOption[] {
  const byId = new Map<string, CustomerChoiceOption>();
  for (const rows of rowSets) {
    if (!Array.isArray(rows)) continue;
    for (const row of rows) {
      const option = optionOf(row);
      if (option === null) continue;
      const seen = byId.get(option.customerId);
      if (seen === undefined || (seen.lastKnown === true && option.lastKnown !== true)) byId.set(option.customerId, option);
    }
  }
  const options = [...byId.values()].sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' }) || a.customerId.localeCompare(b.customerId));
  if (filter.kind === 'customer' && !byId.has(filter.customerId)) options.push({ customerId: filter.customerId, label: filter.label });
  return options;
}

/** The quiet word after a customer's name when the name may be out of date or the customer is retired (`inactive`, `not found`, `last known name`); null when it is plain. */
export function customerChoiceNote(option: CustomerChoiceOption): string | null {
  return customerLineOf({ customerId: option.customerId, label: option.label, state: option.state, lastKnown: option.lastKnown, missing: option.missing }).note;
}

/** Lower case, accents removed, white space collapsed: what a name and what is typed are compared as. */
export function normalizeCustomerSearchText(raw: unknown): string {
  if (typeof raw !== 'string') return '';
  return raw
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

export type CustomerSearchStatus = 'idle' | 'results' | 'none';

export interface CustomerSearchOutcome {
  /** `idle`: nothing typed, so nothing is listed. `results`: at least one match. `none`: something was typed and no customer matches. */
  status: CustomerSearchStatus;
  /** What was typed, trimmed (for "No customers match ..."). */
  query: string;
  /** At most `limit` matches: names that begin with what was typed first, then names with a word that does, then the rest, each group in name order. */
  matches: CustomerChoiceOption[];
  /** How many customers match in all. */
  total: number;
  /** More customers match than are shown. */
  more: boolean;
}

function haystackOf(option: CustomerChoiceOption): string {
  return normalizeCustomerSearchText(option.label);
}

/**
 * The customers that match what was typed, at most `limit` (default 8). Nothing is listed until at least one character is typed. Every word typed must be found in the
 * customer name, ignoring case and accents, in any order.
 */
export function searchCustomerOptions(options: readonly CustomerChoiceOption[], query: unknown, limit: number = CUSTOMER_FILTER_RESULT_LIMIT): CustomerSearchOutcome {
  const cap = Number.isInteger(limit) && limit > 0 ? limit : CUSTOMER_FILTER_RESULT_LIMIT;
  const typed = typeof query === 'string' ? query.replace(/\s+/g, ' ').trim() : '';
  const q = normalizeCustomerSearchText(typed);
  if (q === '') return { status: 'idle', query: typed, matches: [], total: 0, more: false };
  const words = q.split(' ');
  const ranked: { option: CustomerChoiceOption; rank: number; at: number }[] = [];
  options.forEach((option, at) => {
    const hay = haystackOf(option);
    if (!words.every((word) => hay.includes(word))) return;
    const name = normalizeCustomerSearchText(option.label);
    const rank = name.startsWith(q) ? 0 : name.split(' ').some((part) => part.startsWith(words[0])) ? 1 : 2;
    ranked.push({ option, rank, at });
  });
  ranked.sort((a, b) => a.rank - b.rank || a.at - b.at);
  const total = ranked.length;
  const matches = ranked.slice(0, cap).map((r) => r.option);
  return { status: total === 0 ? 'none' : 'results', query: typed, matches, total, more: total > cap };
}

export interface CustomerPickerView {
  status: CustomerSearchStatus;
  /** The line under the matches when more customers match than are shown ("Showing 8 of 21. Keep typing to narrow."); null otherwise. */
  moreNote: string | null;
  /** "No customers match ..." only after something was typed and nothing matches; null otherwise. */
  emptyText: string | null;
  /** The short hint while nothing is typed; null otherwise. */
  hint: string | null;
  /** The polite line for assistive technology; '' while idle. */
  announcement: string;
}

export const CUSTOMER_PICKER_HINT = 'Type a customer name to see matches.';

export function describeCustomerPicker(outcome: CustomerSearchOutcome, limit: number = CUSTOMER_FILTER_RESULT_LIMIT): CustomerPickerView {
  const cap = Number.isInteger(limit) && limit > 0 ? limit : CUSTOMER_FILTER_RESULT_LIMIT;
  if (outcome.status === 'idle') return { status: 'idle', moreNote: null, emptyText: null, hint: CUSTOMER_PICKER_HINT, announcement: '' };
  if (outcome.status === 'none') return { status: 'none', moreNote: null, emptyText: `No customers match "${outcome.query}".`, hint: null, announcement: 'No customers match.' };
  const shown = outcome.matches.length;
  const moreNote = outcome.more ? `Showing ${cap} of ${outcome.total}. Keep typing to narrow.` : null;
  const found = `${shown} ${shown === 1 ? 'customer' : 'customers'} found.`;
  return { status: 'results', moreNote, emptyText: null, hint: null, announcement: `${moreNote !== null ? `${moreNote} ` : `${found} `}Use the arrow keys, then Enter to choose.` };
}

/** The two quick choices, in the order shown. Their values are the filter values `any` and `none`. */
export const CUSTOMER_QUICK_CHOICES: readonly { value: 'any' | 'none'; label: string }[] = Object.freeze([
  { value: 'any' as const, label: 'Any customer' },
  { value: 'none' as const, label: 'Without a customer' },
]);

/** The filter a quick choice stands for. */
export function customerFilterFromQuick(value: 'any' | 'none'): CustomerFilter {
  return value === 'none' ? { kind: 'none' } : ANY_CUSTOMER;
}

/** The filter a chosen customer stands for: the STABLE ID is the value and the name is only for reading. A choice without a usable id or name gives null (nothing is guessed). */
export function customerFilterFromOption(option: unknown): CustomerFilter | null {
  if (option === null || typeof option !== 'object') return null;
  const o = option as { customerId?: unknown; label?: unknown };
  if (typeof o.customerId !== 'string' || o.customerId === '' || o.customerId === CUSTOMER_FILTER_ANY_VALUE || o.customerId === CUSTOMER_FILTER_NONE_VALUE) return null;
  if (typeof o.label !== 'string' || o.label.trim() === '') return null;
  return { kind: 'customer', customerId: o.customerId, label: o.label.trim() };
}

/** What the filter is, in words, for the panel: "All customers", "No customer" or the customer's name. */
export function customerFilterSummary(filter: CustomerFilter): string {
  // The customer's name is SHOWN without the "(FICTIONAL)" test marker; the filter keeps the stored label and its value is the customer id.
  return filter.kind === 'any' ? 'Any customer' : filter.kind === 'none' ? 'Without a customer' : stripTestMarker(filter.label);
}

export interface CustomerFilterChipView {
  /** "Customer: Smith Residence" or "Customer: No customer". */
  text: string;
  /** The Remove button's accessible name. */
  removeLabel: string;
}

/** The removable chip above the table: none for All customers, so the chip always means a filter is on. */
export function describeCustomerFilterChip(filter: CustomerFilter): CustomerFilterChipView | null {
  if (filter.kind === 'any') return null;
  return { text: `${CUSTOMER_FILTER_LABEL}: ${customerFilterSummary(filter)}`, removeLabel: `Remove ${CUSTOMER_FILTER_LABEL} filter` };
}

/** Removing the chip clears ONLY this view's customer filter. Every other view's filter is the same object; nothing else lives in this state. Already off: the same object. */
export function removeCustomerFilter(filters: CustomerFilters, view: CustomerFilterView): CustomerFilters {
  return setCustomerFilter(filters, view, ANY_CUSTOMER);
}

/** Which tasks the picker's options come from. `whole`: the list is whole. `paged`: more pages exist. `open-only`: Team / Company with only the open list read. */
export type CustomerListKind = 'whole' | 'paged' | 'open-only';

/**
 * The sentence under the picker. It names where the customers come from, never claims to search every task, and says that loading more can add other customers where a
 * list is read page by page (Team / Company Completed, Assigned by Me).
 */
export function customerFilterScopeText(kind: CustomerListKind, loadedCount: number, subject: string = 'tasks'): string {
  const n = Number.isInteger(loadedCount) && loadedCount > 0 ? loadedCount : 0;
  const what = `${n} ${n === 1 ? subject.replace(/s$/, '') : subject}`;
  if (kind === 'paged') return `Customers shown are those on the ${what} loaded so far. Loading more can add other customers; this does not search every task.`;
  if (kind === 'open-only') return `Customers shown are those on the ${what} loaded here. Completed tasks are included once Completed is opened.`;
  return `Customers shown are those on the ${what} loaded in this list.`;
}

export interface CustomerFilterEmptyView {
  headline: string;
  detail: string;
  clearLabel: string;
}

/**
 * The empty state when a customer is selected and no row is left. It names the customer filter (and the status choice and the other filters when they also narrow) and offers
 * to clear it; it never says "no tasks" as if nothing existed. `statusText` is the status choice that also narrows ("Status: Waiting"), or null; `otherNarrowing` is true
 * when a search, a department or a quick filter also applies; `complete` is false for a list read page by page.
 */
export function describeCustomerFilterEmpty(filter: CustomerFilter, statusText: string | null, otherNarrowing: boolean, complete: boolean): CustomerFilterEmptyView | null {
  const chip = describeCustomerFilterChip(filter);
  if (chip === null) return null;
  const withStatus = statusText !== null && statusText.trim() !== '' ? ` with ${statusText.trim()}` : '';
  const withOthers = otherNarrowing ? ' and your other filters or search' : '';
  return {
    headline: `No tasks match ${chip.text}${withStatus}${withOthers}.`,
    detail: complete ? `Clear the ${CUSTOMER_FILTER_LABEL} filter to see the tasks again.` : `Only the tasks loaded so far were checked. Load more, or clear the ${CUSTOMER_FILTER_LABEL} filter.`,
    clearLabel: `Clear ${CUSTOMER_FILTER_LABEL} filter`,
  };
}
